import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import DepartmentService from '../../services/department.service';
import { NumberTextField } from '../../components/common';

interface DepartmentFormData {
  name: string;
  code: string;
  description?: string;
  location?: string;
  email?: string;
  phoneNumber?: string;
  budget?: number;
  isActive?: boolean;
}

const DepartmentForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<DepartmentFormData>({
    name: '',
    code: '',
    isActive: true,
  });

  useEffect(() => {
    if (isEdit && id) {
      fetchDepartment(parseInt(id));
    }
  }, [id, isEdit]);

  const fetchDepartment = async (departmentId: number) => {
    try {
      const response = await DepartmentService.getDepartment(departmentId);
      const department = response.data?.data?.attributes;
      if (department) {
        setFormData(department);
      }
    } catch (error) {
      console.error('Error fetching department:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isEdit && id) {
        await DepartmentService.updateDepartment(parseInt(id), formData);
      } else {
        await DepartmentService.createDepartment(formData);
      }
      navigate('/departments');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save department');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit Department' : 'Create Department'}
      </Typography>

      <Paper sx={{ p: 3, mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                required
                label="Department Code"
                name="code"
                value={formData.code}
                onChange={handleChange}
                disabled={isEdit}
              />
              <TextField
                fullWidth
                required
                label="Department Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email || ''}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                label="Phone"
                name="phoneNumber"
                value={formData.phoneNumber || ''}
                onChange={handleChange}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="Location"
                name="location"
                value={formData.location || ''}
                onChange={handleChange}
              />
              <NumberTextField
                fullWidth
                label="Budget"
                name="budget"
                value={formData.budget || ''}
                onChange={handleChange}
                inputProps={{ step: '0.01', min: '0' }}
              />
            </Stack>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              name="description"
              value={formData.description || ''}
              onChange={handleChange}
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="isActive"
                  checked={formData.isActive !== false}
                  onChange={handleChange}
                />
              }
              label="Active"
            />

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/departments')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default DepartmentForm;

